import React from 'react'
import {
  CustomType,
  GoalWithProgress,
  Lang,
  ProgressType,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
} from '../../services/api.types'
import { t } from 'ttag'
import { formatThousands } from '../../common/helpers/translations'
import { ReactComponent as GoalIcon } from '../../assets/v3/icons/ranking/goal.svg'
import { ReactComponent as GoalAchievedIcon } from '../../assets/v3/icons/ranking/goal-achieved.svg'
import { getCustomTypeText, getCustomUnitText } from '../../services/CustomTypes'
import { getChallengeDurationInDays } from '../../common/helpers/date'
import {
  adjustPercentageForActivityIndex,
  ChallengeGoalType,
  getChallengeGoalUnits,
  getChallengeProgressCurrently,
  getChallengeProgressGoal,
  getChallengeProgressPercentage,
  getChallengeProgressUnits,
  getPersistenceDailyTotal,
  isActivityIndexType,
  isAutomatedType,
  isCollectiveType,
  isCustomType,
  isDistanceType,
  isPersistenceType,
  parseAndExtractGoalConfig,
} from '../../common/helpers/challenges'
import YumGrid from '../YumGrid/YumGrid'
import { darkBlue, lightGreen, gray } from '../../styles/material-ui-theme-v2'
import YumConsistencyPills, { ConsistencyProgress } from './components/YumConsistencyPills/YumConsistencyPills'
import { currentLang, isRtl } from '../../i18nInit'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { GoalConfig } from '../../views/ChallengeAdmin/ChallengeAdmin'

// Re-export GoalType from challenges helper for backward compatibility
export type GoalType = ChallengeGoalType

export interface IYumChallengeGoalProps {
  goal: GoalWithProgress
  dailyDiary: UserDailyDiaryProgress[]
  customProgress?: UserCustomDailyProgress[]
  progressResults: UserDailyProgress[]
  // These can be removed later
  showDetails: boolean
  toggleShowDetails: () => void
  isMeView: boolean
  isLeaderboard: boolean
  // Optional: specify which goal type to display (individual, team, country, group, collective)
  // If not provided, uses the main goal (backward compatible)
  goalType?: GoalType
  // Required when goalType is 'group' - specifies which division category
  groupCategoryId?: string
  // Optional: use light gray background instead of default blue/green
  lightGrayBackground?: boolean
}

const useStyles = makeStyles((theme) => ({
  main: {
    padding: '10px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    color: 'white',
  },
  header: {
    display: 'flex',
    //gap: 10,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    //marginBottom: '32px',
    userSelect: 'none',
  },
  headerIcon: {
    flexShrink: 0,
    marginRight: isRtl ? 0 : '10px',
    marginLeft: isRtl ? '10px' : 0,
  },
  totalGoalLabel: {
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '14px',
  },
  totalGoalText: {
    fontWeight: 500,
    fontSize: 15,
    lineHeight: '18px',
  },
  progressCurrently: {
    fontWeight: 500,
    fontSize: 28,
    lineHeight: '33px',
  },
  progressGoal: {
    fontWeight: 500,
    fontSize: 28,
    lineHeight: '33px',
    opacity: 0.8,
  },
  progressUnits: {
    fontWeight: 500,
    fontSize: 18,
    lineHeight: '21px',
    opacity: 0.8,
  },
  progressBar: {
    borderRadius: 10,
    height: 8,
    marginTop: 8,
    background: 'rgba(255, 255, 255, 0.4)',
  },
  progressBarInner: {
    borderRadius: 10,
    height: 8,
    marginTop: 8,
    background: 'white',
    transition: 'all 0.2s ease-out',
  },

  footer: {
    display: 'flex',
    //gap: 10,
    flexDirection: 'column',
    position: 'relative',
    padding: '10px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  footerInner: {
    display: 'flex',
    justifyContent: 'space-between',
    //gap: 10,
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '16px',
  },
  challengeLength: {
    paddingTop: '1px',
  },
  footerButton: {
    display: 'inline-block',
    padding: '0 8px',
    lineHeight: '16px',
    fontSize: 12,
    fontWeight: 'bold',
    borderRadius: 6,
    width: 'auto',
    minWidth: 'auto',
    margin: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    height: '20px',
  },
}))

export default function YumChallengeGoal(props: IYumChallengeGoalProps) {
  const classes = useStyles()
  const {
    goal,
    dailyDiary,
    customProgress,
    progressResults,
    showDetails,
    toggleShowDetails,
    isMeView,
    isLeaderboard,
    goalType,
    groupCategoryId,
    lightGrayBackground,
  } = props

  // Parse and extract goal-specific configuration based on goalType
  // If goalType is not provided, uses the main goal (backward compatible)
  const { goalConfig } = parseAndExtractGoalConfig(goal.goals, goalType, groupCategoryId)

  // Use goal config values if available, otherwise fall back to main goal values
  // This allows the component to work with both old and new goal structures
  const {
    id: goalId,
    customName,
    customType,
    customUnit,
    customGoal: mainCustomGoal,
    persistenceGoal: mainPersistenceGoal,
    dailyPersistenceGoal: mainDailyPersistenceGoal,
    progressType,
    participating,
    startDate,
    endDate,
    activityFormulaTarget: mainActivityFormulaTarget,
    collectivePercentage,
    manualEntryEnabled,
    stepsGoal: mainStepsGoal,
  } = goal

  // Helper function to convert GoalConfig value (number | '') to number or null
  const getNumericValue = (value: number | ''): number | null => {
    return value !== '' && typeof value === 'number' ? value : null
  }

  // Use goal config values when available, otherwise use main goal values
  const customGoal = goalConfig?.totalGoal ? (getNumericValue(goalConfig.totalGoal) ?? mainCustomGoal) : mainCustomGoal
  const persistenceGoal = goalConfig?.persistenceGoal
    ? (getNumericValue(goalConfig.persistenceGoal) ?? mainPersistenceGoal)
    : mainPersistenceGoal
  // const dailyPersistenceGoal = goalConfig?.dailyGoal
  //   ? (getNumericValue(goalConfig.dailyGoal) ?? mainDailyPersistenceGoal)
  //   : mainDailyPersistenceGoal
  const dailyPersistenceGoal = mainDailyPersistenceGoal
  const activityFormulaTarget = goalConfig?.indexTarget
    ? (getNumericValue(goalConfig.indexTarget) ?? mainActivityFormulaTarget)
    : mainActivityFormulaTarget
  const stepsGoal = goalConfig?.totalGoal ? (getNumericValue(goalConfig.totalGoal) ?? mainStepsGoal) : mainStepsGoal
  // Create a modified goal object with goal-specific values for helper functions
  // This allows us to reuse existing helper functions while using goal config values
  const goalWithConfig: GoalWithProgress = {
    ...goal,
    customGoal: customGoal ?? null,
    persistenceGoal: persistenceGoal ?? null,
    dailyPersistenceGoal: dailyPersistenceGoal ?? null,
    activityFormulaTarget: activityFormulaTarget ?? null,
    stepsGoal: stepsGoal ?? 0,
  }

  const isPersistenceCustom = progressType === ProgressType.PERSISTENCE_CUSTOM
  const isPersistenceCustomOrBinary =
    progressType === ProgressType.PERSISTENCE_CUSTOM || progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY
  const isCustom = isCustomType(progressType)
  const isCustomCustom = customType === CustomType.CUSTOM
  const isPersistence = isPersistenceType(progressType)
  const isActivityIndex = isActivityIndexType(progressType)
  const isTotalActivityIndex = [ProgressType.ALL].includes(progressType)
  const isPersistenceOrCustom = isPersistence || isCustom

  const customUnits = getCustomUnitText(customType, customType, customUnit) as string
  const daysNumber = getChallengeDurationInDays(startDate, endDate)

  const isDistance = isDistanceType(customType)
  const isAutomated = isAutomatedType(customType)
  const isBinary = progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY

  // Get goal type label for display
  const getGoalTypeLabel = (): string => {
    if (!goalType) {
      // Default behavior: use collective or total goal label
      return isCollective ? t`Collective goal` : t`Total goal`
    }
    switch (goalType) {
      case 'individual':
        return t`Individual goal`
      case 'team':
        return t`Team goal`
      case 'country':
        return t`Country goal`
      case 'group':
        return t`Group goal`
      case 'collective':
        return t`Collective goal`
      default:
        return t`Total goal`
    }
  }

  const getPillsProgress = (manualEntryEnabled: boolean): ConsistencyProgress[] => {
    const dailyGoal = isBinary || dailyPersistenceGoal === null ? 1 : dailyPersistenceGoal || 0
    if (isActivityIndex) {
      return progressResults.length !== daysNumber
        ? []
        : progressResults.map((item, keyIndex) => {
            const total = getPersistenceDailyTotal(item, progressType, manualEntryEnabled)
            const { date } = item
            const achieved = total >= dailyGoal && participating
            return { date, achieved }
          })
    } else if (isCustomCustom || isPoints) {
      return customProgress?.length !== daysNumber
        ? []
        : customProgress.map((item, keyIndex) => {
            const { date, value } = item
            const achieved = value >= dailyGoal && participating
            return { date, achieved }
          })
    } else {
      return dailyDiary?.length !== daysNumber
        ? []
        : dailyDiary.map((item, keyIndex) => {
            const { date, sum, integrationValue } = item
            const achieved = (manualEntryEnabled ? sum >= dailyGoal : integrationValue >= dailyGoal) && participating
            return { date, achieved }
          })
    }
  }

  // const isActivityIndex = progressType === ProgressType.ALL
  const isCollective = isCollectiveType(progressType)
  const isPoints = customType === CustomType.POINTS
  // console.log('isPoints', isPoints, customType)

  // IMPORTANT NUMBERS SHOWN
  // Use goalWithConfig to get progress values based on goal config if available
  const showDigits = isDistance && !isCollective && !isPersistenceCustomOrBinary ? 2 : undefined
  const progressCurrently = formatThousands(
    getChallengeProgressCurrently(goalWithConfig, goalType, groupCategoryId),
    showDigits,
  )
  const progressGoal = formatThousands(getChallengeProgressGoal(goalWithConfig))
  const progressUnits = getChallengeProgressUnits(goalWithConfig)
  // --- //

  const getTotalGoalText = () => {
    if (isTotalActivityIndex) {
      return `${progressGoal}%`
    } else if (isPersistence) {
      const persistenceGoalValue = persistenceGoal || 0
      return currentLang === Lang.EN
        ? t`Achieve daily goal for ${persistenceGoalValue} days`
        : t`Achieve daily target for ${persistenceGoalValue} days`
    } else {
      // Use goal config values for goal text
      const units = getChallengeGoalUnits(goalWithConfig)
      return `${progressGoal} ${units}`
    }
  }

  // Calculate percentage using helper function, then adjust for activity index if needed
  let percentageDone: any = adjustPercentageForActivityIndex(
    getChallengeProgressPercentage(goalWithConfig, goalType),
    isTotalActivityIndex,
    activityFormulaTarget,
  )

  const progressPercentage = percentageDone > 100 ? 100 : percentageDone

  const showProgress = isCollective || !isLeaderboard
  const goalAchieved = progressPercentage === 100 && showProgress

  percentageDone = formatThousands(percentageDone, 0)

  const isTotalPoints = customType === CustomType.TOTAL_POINTS

  const activityName = isTotalActivityIndex
    ? t`Activity index`
    : isTotalPoints
      ? getCustomTypeText(CustomType.POINTS)
      : getCustomTypeText(customType, customName)

  const pillsProgress = getPillsProgress(manualEntryEnabled)

  // If goalType is specified but goal config is not enabled or doesn't exist, don't render
  if (goalType && (!goalConfig || !goalConfig.enabled)) {
    return null
  }

  return (
    <div>
      <div
        className={classes.main}
        style={{
          color: goalAchieved ? darkBlue : 'white',
          backgroundColor: goalAchieved ? lightGreen : darkBlue,
          borderBottomLeftRadius: !isPersistence ? '8px' : '0px',
          borderBottomRightRadius: !isPersistence ? '8px' : '0px',
          marginBottom: !isPersistence ? (lightGrayBackground ? '12px' : '16px') : '0px',
        }}
      >
        <div className={classes.header}>
          <div className={classes.headerIcon}>{goalAchieved ? <GoalAchievedIcon /> : <GoalIcon />}</div>
          <YumGrid>
            <div>
              <>
                <div className={classes.totalGoalLabel}>
                  {getGoalTypeLabel()}
                  {activityName && !isCustomCustom && !isPersistence && (isAutomated || isTotalActivityIndex)
                    ? ` (${activityName.toLowerCase()})`
                    : ''}
                </div>
                <div className={classes.totalGoalText}>{getTotalGoalText()}</div>
              </>
            </div>
          </YumGrid>
        </div>
        {showProgress && (
          <>
            <YumGrid style={{ marginTop: lightGrayBackground ? '16px' : '32px' }}>
              <div>
                <span className={classes.progressCurrently}>{progressCurrently}</span>
                {!isPersistence && !isTotalActivityIndex ? (
                  <>
                    <span className={classes.progressUnits}> {progressUnits}</span>
                  </>
                ) : (
                  <>
                    <span className={classes.progressGoal}> / {progressGoal}</span>{' '}
                    <span className={classes.progressUnits}>{progressUnits}</span>
                  </>
                )}
              </div>
              {!isTotalActivityIndex && (
                <div style={{ whiteSpace: 'nowrap' }}>
                  <span className={classes.progressCurrently}>{percentageDone}</span>
                  <span className={classes.progressUnits}> %</span>
                </div>
              )}
            </YumGrid>
            <div className={classes.progressBar}>
              <div
                className={classes.progressBarInner}
                style={{ width: `${progressPercentage}%`, backgroundColor: goalAchieved ? darkBlue : 'white' }}
              />
            </div>
          </>
        )}
      </div>

      {isPersistence && (
        <div
          className={classes.footer}
          style={{
            backgroundColor: lightGrayBackground ? gray : 'white',
            marginBottom: '16px',
          }}
        >
          <div className={classes.footerInner}>
            <span className={classes.challengeLength}>{t`Challenge length: ${daysNumber} days`}</span>
          </div>
          {isPersistence && pillsProgress && pillsProgress.length > 0 ? (
            <YumConsistencyPills progress={pillsProgress} />
          ) : null}
        </div>
      )}
    </div>
  )
}
